import { memo, useCallback, useMemo, useRef } from 'react';
import { cn } from '@divlab/divanui';

import useTranslation from '@Queries/useTranslation';
import useMedias from '@Hooks/useMedias';
import AnimatedSegment from '@Components/AnimatedSegment';
import Button from '@UI/Button';
import styles from './Stage.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { ProcessItemData } from '@Pages/PageExpressDesign/type';

export interface Props extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  items: ProcessItemData[];
  progress?: number;
  onScrollToBlock?: (e: MouseEvent, anchorName: string, tariffId?: string) => void;
}

interface StylesOfStepType {
  style: { [key: number]: number };
  step: number;
}

const Stage: FC<Props> = (props) => {
  const { className, progress, items, onScrollToBlock, ...restProps } = props;
  const { isDesktop } = useMedias();
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const countStep = 3;
  const widthStep = 100 / countStep;

  const stylesOfStep = useMemo(() => {
    const initialStyle: StylesOfStepType = { style: { 0: 0, 1: 0, 2: 0 }, step: 1 };

    if (progress < widthStep) {
      initialStyle.step = 1;

      initialStyle.style[0] = progress * 3;
      initialStyle.style[1] = 0;
      initialStyle.style[2] = 0;
    } else if (progress > widthStep * 2) {
      initialStyle.step = 3;

      initialStyle.style[0] = 100;
      initialStyle.style[1] = 100;
      initialStyle.style[2] = ((progress - widthStep * 2) * 100) / widthStep;
    } else {
      initialStyle.step = 2;

      initialStyle.style[0] = 100;
      initialStyle.style[1] = ((progress - widthStep) * 100) / widthStep;
      initialStyle.style[2] = 0;
    }

    return initialStyle;
  }, [progress, widthStep]);

  const handleScroll = useCallback(
    (e: MouseEvent) => {
      if (onScrollToBlock) onScrollToBlock(e, 'express-design-second-application');
    },
    [onScrollToBlock],
  );

  return (
    <div {...restProps} className={cn(styles.stage, className)}>
      <div className={cn(styles.container)}>
        <AnimatedSegment progress={progress} end={100}>
          <div ref={ref} className={styles.content}>
            <div className={styles.title}>{t('process-work')}</div>
            <div className={styles.step}>
              {items.map((item, index) => {
                return (
                  <div className={styles.item} key={item.id}>
                    {index !== items.length - 1 && (
                      <div
                        className={styles.progress}
                        style={
                          isDesktop
                            ? { height: `${stylesOfStep.style[index]}%` }
                            : { width: `${stylesOfStep.style[index]}%` }
                        }
                      ></div>
                    )}
                    <div className={styles.positionWrapper}>
                      <div
                        className={cn(styles.position, {
                          [styles.active]: progress === 100 || stylesOfStep.step >= item.id,
                        })}
                      >
                        {item.id}
                      </div>
                    </div>
                    <div className={styles.textContent} key={item.id}>
                      <div className={styles.subtitle}>{item.title}</div>
                      <div className={styles.text}>{item.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button className={styles.button} onClick={handleScroll}>
              {t('send-request')}
            </Button>
          </div>
        </AnimatedSegment>
      </div>
    </div>
  );
};

export default memo(Stage);
